import clsx from "clsx";
import { Badge, Icon, Text, View } from "../base";
import { AgentEnvironmentPill } from "../AgentEnvironmentPill";
import { EvaluationStatusPill } from "../EvaluationStatusPill";
import { getJudgeStatusIcon, getExtractionStatusIcon } from "./EvaluationDetailView";
import type { Evaluation, EvaluationStatus, JudgeRuling, AgentExtraction } from "../../redux/reducers/evaluationSlices";
import type { AgentEnvironment } from "../../redux/reducers/agentEnvironmentSlice";
import type { Evaluator } from "../../redux/reducers/evaluatorSlice";
import { AgentSessionOriginPill } from "../AgentSessionOriginPill";

interface EvaluationListItemProps {
  evaluation: Evaluation;
  evaluator: Evaluator | null | undefined;
  agentEnvironmentEntities: Record<string, AgentEnvironment>;
  isActive: boolean;
  isFirstItem: boolean;
  onClick: (evaluationId: string) => void;
  sessionEnvironmentId?: string | null;
  sessionOrigin?: string;
}

const formatDate = (date: Date | string) => new Date(date).toLocaleString();

const getEnvironmentNameFromEntities = (entities: Record<string, AgentEnvironment>, environmentId?: string | null) => {
  if (!environmentId) {
    return "Unknown environment";
  }

  return entities[environmentId]?.name || environmentId;
};

export const EvaluationListItem = ({
  evaluation,
  evaluator,
  agentEnvironmentEntities,
  isActive,
  isFirstItem,
  onClick,
  sessionEnvironmentId,
  sessionOrigin,
}: EvaluationListItemProps) => {
  const evaluatorName = evaluator?.title || `Evaluator ${evaluation.evaluatorId}`;
  const envId = sessionEnvironmentId ?? evaluation.session?.agentEnvironmentId;
  const environmentName = envId ? getEnvironmentNameFromEntities(agentEnvironmentEntities, envId) : evaluation.session?.environment || "Unknown environment";
  const origin = sessionOrigin ?? evaluation.session?.origin ?? "Unknown";
  const statusIcon = evaluation.evalType === "judge" ? getJudgeStatusIcon(evaluation as JudgeRuling) : getExtractionStatusIcon(evaluation as AgentExtraction);

  return (
    <View
      onClick={() => onClick(evaluation.id)}
      className={clsx(
        "py-3 px-4 cursor-pointer transition-colors",
        isActive ? "bg-indigo-100 dark:bg-indigo-900" : "bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700",
        !isFirstItem && "border-t border-gray-200 dark:border-gray-700",
      )}
    >
      <View className="flex flex-col">
        <View className="flex items-center gap-2">
          <Icon
            title={statusIcon?.title}
            name={statusIcon?.name || "assessment"}
            size={16}
            className={statusIcon?.className || "text-indigo-500"}
          />
          <Text className="text-sm font-semibold text-gray-900 dark:text-white">{formatDate(evaluation.createdAt)}</Text>
          <AgentEnvironmentPill
            environment={environmentName}
            size="small"
            variant="muted"
          />
          {evaluation.status ? (
            <View className="ml-auto">
              <EvaluationStatusPill status={evaluation.status} />
            </View>
          ) : null}
        </View>
        <View className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-600 dark:text-gray-300">
          <Text className="font-medium text-xs text-gray-700 dark:text-gray-200">{evaluatorName}</Text>
          <Badge
            variant={evaluation.evalType === "judge" ? "warning" : "brand"}
            className="capitalize"
          >
            {evaluation.evalType}
          </Badge>
          {origin && origin.toLowerCase() !== "unknown" ? <AgentSessionOriginPill origin={origin} /> : null}
          {evaluation.trigger && (
            <Badge
              variant="default"
              className="capitalize ml-auto"
              title="Trigger"
            >
              {evaluation.trigger}
            </Badge>
          )}
        </View>
      </View>
    </View>
  );
};
